import DownloadIcon from "@assets/images/down-arrow.svg";
import { Button, IconButton } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Container from "@saleor/components/Container";
import Filter from "@saleor/components/Filter";
import PageHeader from "@saleor/components/PageHeader";
import SearchInput from "@saleor/components/SearchBar/SearchInput";
import FilterTabs, { FilterTab } from "@saleor/components/TableFilter";
import { sectionNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import {
  createFilterStructureForDispatcher,
  createFilterStructureForNonDispatcher,
  createFilterStructureForOmiAdmin,
  RemittanceRunnerFilterKeys,
  RemittanceRunnerFilterOpts
} from "@saleor/remittanceRunner/filters/filters";
import { RemittanceRunnerOrders_remittanceOrders } from "@saleor/remittanceRunner/types/RemittanceRunnerOrders";
import { RemittanceRunnerRemittances_remittances } from "@saleor/remittanceRunner/types/RemittanceRunnerRemittances";
import { RemittanceMotherOrder } from "@saleor/remittanceRunner/utils/formDataTypes";
import { FilterMenuProps, PageListProps } from "@saleor/types";
import { RemittanceStatusEnum } from "@saleor/types/globalTypes";
import { canManageOverrideRemittance } from "@saleor/utils/handlers/userHandler";
import * as React from "react";
import { useIntl } from "react-intl";

import RemittanceRunnerNoPending from "../RemittanceRunnerNoPending";
import RemittanceRunnerNoRecordsFound from "../RemittanceRunnerNoRecordsFound";
import RemittanceRunnerRemitButton from "../RemittanceRunnerRemitButton";
import RemittanceRunnerRemittanceDisabled from "../RemittanceRunnerRemittanceIsDisabled";
import RemittanceRunnerRemittanceIsLoading from "../RemittanceRunnerRemittanceIsLoading";
import RemittanceRunnerTableDispatcher from "../RemittanceRunnerTableDispatcher";
import RemittanceRunnerTableNonDispatcher from "../RemittanceRunnerTableNonDispatcher";
import RemittanceStatusOverride from "../RemittanceStatusOverride";

const useStyles = makeStyles(
  theme => ({
    buttonsRight: {
      display: "inline",
      marginLeft: "auto",
      padding: "12px"
    },
    divFilterSearch: {
      borderBottom: `1px solid ${theme.palette.divider}`,
      display: "flex",
      flexWrap: "wrap",
      padding: theme.spacing(1, 3)
    },
    iconDownloadRight: {
      paddingRight: 20
    },
    statusOverrideBtn: {
      "& span": {
        color: "#0030FF"
      },
      backgroundColor: "#FFDB00",
      padding: "5px 10px"
    },
    statusOverrideBtnDisabled: {
      "& span": {
        color: "#0030FF"
      },
      backgroundColor: "#ffdb004d",
      opacity: 0.7,
      padding: "5px 10px",
      pointerEvents: "none"
    }
  }),
  {
    name: "RemittanceRunnerListPage"
  }
);

interface RemittanceRunnerPageProp
  extends PageListProps,
    FilterMenuProps<RemittanceRunnerFilterKeys, RemittanceRunnerFilterOpts> {
  search: string;
  searchPlaceholder: string;
  onSearchChange: (val) => void;
  remittances: RemittanceRunnerRemittances_remittances;
  remittanceOrders: RemittanceRunnerOrders_remittanceOrders;
  isDispatcher: boolean;
  isOmiAdmin: boolean;
  isSuperUser: boolean;
  isEnableMall: boolean;
  isEmptyMall: boolean;
  isLoadingRemittanceOrders: boolean;
  isLoadingRemittance: boolean;
  currentTab: number;
  tabChanged: (tabIndex: any, tabValue: any) => void;
  onRefetchOrders: () => void;
  onDownloadClick: () => void;
}

export const RemittanceRunnerListPage: React.FC<RemittanceRunnerPageProp> = props => {
  const {
    currencySymbol,
    onFilterChange,
    search,
    searchPlaceholder,
    onSearchChange,
    filterOpts,
    remittances,
    remittanceOrders,
    isDispatcher,
    isOmiAdmin,
    isSuperUser,
    isEnableMall,
    isEmptyMall,
    isLoadingRemittanceOrders,
    isLoadingRemittance,
    currentTab,
    tabChanged,
    onRefetchOrders,
    onDownloadClick,
    ...listProps
  } = props;
  const intl = useIntl();
  const classes = useStyles({});
  const hasNoPending =
    remittances?.totalCount === 0 || remittanceOrders?.totalCount === 0;
  const userCanOverrideRemittance = canManageOverrideRemittance();
  const [openStatusOverride, setOpenStatusOverride] = React.useState(false);
  const [motherOrderList, setMotherOrderList] = React.useState<string[]>([]);
  const [motherOrderCode, setMotherOrderCode] = React.useState<
    RemittanceMotherOrder
  >({
    courier: "",
    orderCode: ""
  });

  const filterMenu = () => {
    if (isOmiAdmin || isSuperUser) {
      return createFilterStructureForOmiAdmin(intl, filterOpts);
    } else if (!isDispatcher) {
      return createFilterStructureForNonDispatcher(intl, filterOpts);
    } else {
      return createFilterStructureForDispatcher(intl, filterOpts);
    }
  };

  const getContentToRender = () => {
    let contentToRender;

    if (isLoadingRemittanceOrders || isLoadingRemittance) {
      contentToRender = (
        <RemittanceRunnerRemittanceIsLoading
          numberOfColumns={7}
          isTableFormat={true}
        />
      );
    } else if (!isEnableMall && !isEmptyMall) {
      contentToRender = <RemittanceRunnerRemittanceDisabled />;
    } else if (hasNoPending) {
      if (!search && isDispatcher) {
        contentToRender = <RemittanceRunnerNoPending />;
      } else {
        contentToRender = <RemittanceRunnerNoRecordsFound />;
      }
    } else if (isEmptyMall || !isDispatcher) {
      contentToRender = (
        <RemittanceRunnerTableNonDispatcher
          remittances={remittanceOrders}
          loadingOrders={isLoadingRemittanceOrders}
          disabled={false}
          isOmiAdmin={isOmiAdmin}
          isSuperUser={isSuperUser}
          userCanOverrideRemittance={userCanOverrideRemittance}
          onRefetchOrders={onRefetchOrders}
          remittanceIdList={motherOrderList}
          setMotherOrderList={setMotherOrderList}
          setMotherOrderCode={setMotherOrderCode}
          {...listProps}
        />
      );
    } else {
      contentToRender = (
        <RemittanceRunnerTableDispatcher
          remittances={remittances}
          disabled={false}
          {...listProps}
        />
      );
    }

    return contentToRender;
  };

  return (
    <Container>
      <PageHeader title={intl.formatMessage(sectionNames.remittanceRunner)}>
        {maybe(
          () =>
            (isDispatcher && isEnableMall && !hasNoPending) ||
            (isDispatcher && isEnableMall && hasNoPending && !!search)
        ) && <RemittanceRunnerRemitButton />}
      </PageHeader>
      <Card>
        <FilterTabs currentTab={currentTab}>
          <FilterTab
            onClick={() => tabChanged(0, [])}
            label={intl.formatMessage({
              defaultMessage: "All"
            })}
          />
          <FilterTab
            onClick={() => tabChanged(1, [RemittanceStatusEnum.TO_REMIT])}
            label={intl.formatMessage({
              defaultMessage: "To Remit"
            })}
          />
          <FilterTab
            onClick={() =>
              tabChanged(2, [RemittanceStatusEnum.FOR_BANK_CONFIRMATION])
            }
            label={intl.formatMessage({
              defaultMessage: "For Confirmation"
            })}
          />
          <FilterTab
            onClick={() => tabChanged(3, [RemittanceStatusEnum.REMITTED])}
            label={intl.formatMessage({
              defaultMessage: "Remitted"
            })}
          />
          {maybe(
            () => !isLoadingRemittance && !isDispatcher && !isOmiAdmin
          ) && (
            <div className={classes.buttonsRight}>
              {userCanOverrideRemittance && (
                <Button
                  className={
                    !userCanOverrideRemittance || motherOrderList.length === 0
                      ? classes.statusOverrideBtnDisabled
                      : classes.statusOverrideBtn
                  }
                  size="small"
                  disabled={
                    !userCanOverrideRemittance || motherOrderList.length === 0
                  }
                  onClick={() => setOpenStatusOverride(true)}
                >
                  Status Override
                </Button>
              )}

              <IconButton
                className={classes.iconDownloadRight}
                onClick={onDownloadClick}
              >
                <img alt="Download icon" src={DownloadIcon} width={20} />
              </IconButton>
            </div>
          )}
        </FilterTabs>

        <div className={classes.divFilterSearch}>
          <Filter
            currencySymbol={currencySymbol}
            menu={filterMenu()}
            onFilterAdd={onFilterChange}
            sorted={false}
          />
          <SearchInput
            initialSearch={search}
            placeholder={searchPlaceholder}
            onSearchChange={val => {
              onSearchChange(val);
              setMotherOrderList([]);
              setMotherOrderCode(prev => ({
                ...prev,
                courier: "",
                orderCode: ""
              }));
            }}
          />
        </div>

        {getContentToRender()}
      </Card>
      {openStatusOverride && (
        <RemittanceStatusOverride
          open={openStatusOverride}
          onClose={() => setOpenStatusOverride(false)}
          remittanceOrderCourier={motherOrderCode}
          setRemittanceOrderCourier={setMotherOrderCode}
          bulkOrderIds={motherOrderList}
          setBulkOrderIds={setMotherOrderList}
          onRefetchRemittanceOrder={onRefetchOrders}
        />
      )}
    </Container>
  );
};

export default RemittanceRunnerListPage;
